/**
 * The chassis tokens.
 *
 * Exact values from the design handoff (`docs/design/README.md`). Declared here
 * as well as in `tokens.css` so the contrast gate can reason about them on the
 * server, where there is no stylesheet.
 *
 * These are FIXED. They ship, and they are never derived from a team's colors.
 * The whole design thesis is a quiet chassis that makes any two team colors look
 * intentional, which only works if the chassis does not move.
 */

export const CHASSIS = {
  /** App background. Cool paper white with a faint warm cast. Not cream. */
  paper: '#FAF9F5',
  /** Primary text, heavy rules, dark buttons. Blue-black, never pure black. */
  ink: '#14182A',
  /** Secondary text, labels, captions. Passes AA on paper at all sizes. */
  inkMuted: '#4A4E61',
  /** Decorative ONLY: dots, dashed borders, truncation rows. Never body text. */
  inkFaint: '#8B8FA3',
  /** Hairlines, 1px borders, default button borders. Should almost disappear. */
  rule: '#D9DAE0',
  surface: '#FAF9F5',
  surfaceRaised: '#FFFFFF',
  surfaceSunken: '#F1F0EA',
} as const;

/**
 * Semantic colors.
 *
 * Semantic ONLY. Never derived from team colors: a red team must not get red
 * success states, and a teal team must not get teal warnings. Rubric C7.
 */
export const SEMANTIC = {
  /** Confirmed, present, MADE, positive plus-minus, synced. */
  success: '#1F7A48',
  /** Bonus, foul trouble, unattributed plays. A text and stroke value. */
  warning: '#96591A',
  warningFill: '#FBE9C6',
  warningTint: '#FDF6E9',
  warningDeep: '#7A4712',
  /**
   * Destructive only, and AA-failure notices. Deliberately orange-leaning so it
   * never reads as a team's scarlet.
   */
  danger: '#A8341F',
  /** AWAY tag, MISS, THEM, OUT chips. Opponent and neutral things. */
  neutralGraphite: '#55555D',
} as const;

/**
 * The type scale, in px. Nothing between these values.
 *
 * 72 is reserved for the live score and nothing else.
 */
export const TYPE_SCALE = [12, 14, 16, 18, 21, 28, 38, 52, 72] as const;
export type TypeScaleStep = (typeof TYPE_SCALE)[number];

export const FONT = {
  display: "'Archivo', system-ui, sans-serif",
  body: "'Public Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
} as const;

/**
 * Motion budget, in ms. BUILD_PLAN 9.5 and the design handoff agree exactly.
 *
 * One orchestrated moment, not scattered effects. The substitution swap is the
 * only place the animation budget is spent, because it confirms an action a
 * scorekeeper cannot afford to get wrong.
 */
export const MOTION = {
  substitutionSwap: 220,
  scoreRoll: 120,
  sheet: 240,
  sheetEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  default: 150,
  /** The selection auto-clear in the scorebook. Drains, then drops. */
  selectionTimeout: 4000,
} as const;

/**
 * Minimum touch targets, in px.
 *
 * The scorebook figure is not a rounded-up 44. It is the size a thumb hits
 * while its owner is watching a ten-year-old drive baseline.
 */
export const TOUCH_TARGET = {
  minimum: 44,
  scorebook: 64,
} as const;

/**
 * Every chassis and semantic custom property, by the name a stylesheet declares.
 *
 * The `--color-` prefix is Tailwind v4's convention and, under ADR-0003, a
 * `@theme` entry IS the custom property the generated utilities read.
 *
 * This list exists because the same defect appeared THREE times: code reading
 * `--team-primary` while the stylesheet declared `--color-team-primary`, so the
 * variable resolved to nothing. Each time it was invisible, because every
 * reference carried a fallback to a compile-time constant. The component looked
 * correct in isolation and simply ignored the theme.
 *
 * Three occurrences is the argument for a machine check rather than a fourth
 * fix. `styles.test.ts` asserts every property the primitives read appears
 * here, and `apps/web` asserts every property here is declared in `tokens.css`.
 * Between them a name that resolves to nothing is a red build.
 */
export const CHASSIS_VARIABLE_NAMES: readonly string[] = [
  '--color-paper',
  '--color-ink',
  '--color-ink-muted',
  '--color-ink-faint',
  '--color-rule',
  '--color-surface',
  '--color-surface-raised',
  '--color-surface-sunken',
  '--color-success',
  '--color-warning',
  '--color-warning-fill',
  '--color-warning-tint',
  '--color-warning-deep',
  '--color-danger',
  '--color-neutral-graphite',
];

/** Breakpoint below which Dialogs render as bottom Sheets. Rubric C10. */
export const SHEET_BREAKPOINT_PX = 640;

/** The narrowest supported viewport. Rubric C12. */
export const MIN_VIEWPORT_PX = 360;
